import {Component, Expressions} from '@packages/systems/wfdl';

import {weakMemo} from '@packages/utilities/memo';

import {
  isCommerceCartWrapper,
  isCommerceCheckoutFormContainer,
  isCommerceOrderConfirmationContainer,
  isCommercePaypalCheckoutFormContainer,
  isSearchResultItem,
  isPasswordPageForm,
  isSearchResultWrapper,
  isSearchForm,
  isLocalesWrapper,
  isDynamicList,
  isSlot,
  isPagination,
} from '@packages/utilities/elementTypeUtils';

import {
  isComponentRoot,
  isUserComponentRoot,
} from '@packages/utilities/core-elements';

import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';

export const isInsideCommerceCartWrapper = (
  element: ExpressionElement,
  root: Expression
): boolean =>
  Boolean(Expressions.closestElement(root, element.id, isCommerceCartWrapper));

export const isInsideCommerceCheckoutFormContainer = (
  element: ExpressionElement,
  root: Expression
): boolean =>
  Boolean(
    Expressions.closestElement(
      root,
      element.id,
      isCommerceCheckoutFormContainer
    )
  );

export const isInsideCommerceOrderConfirmationContainer = (
  element: ExpressionElement,
  root: Expression
): boolean =>
  Boolean(
    Expressions.closestElement(
      root,
      element.id,
      isCommerceOrderConfirmationContainer
    )
  );

export const isInsideCommercePaypalCheckoutFormContainer = (
  element: ExpressionElement,
  root: Expression
): boolean =>
  Boolean(
    Expressions.closestElement(
      root,
      element.id,
      isCommercePaypalCheckoutFormContainer
    )
  );
// Returns true if element or any of its ancestors match the predicate.
export const isInside: (
  predicate: (arg1: ExpressionElement) => boolean
) => (root: Expression) => (element: ExpressionElement) => boolean = weakMemo(
  (predicate: (arg1: ExpressionElement) => boolean) =>
    (root: Expression) =>
    (element: ExpressionElement) =>
      Boolean(Expressions.closestElement(root, element.id, predicate))
);

export const isInsideSearchResultItem: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isSearchResultItem);
export const isInsidePasswordPageForm: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isPasswordPageForm);
export const isInsideSearchResultWrapper: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isSearchResultWrapper);
export const isInsideSearchForm: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isSearchForm);

/**
 * Returns a boolean whether the element is inside the given component root,
 * this could be a child of the component root or an instance of a nested component
 */
export const isInsideComponentRoot = (
  componentMap: Component.ComponentMap
): ((root: Expression) => (element: ExpressionElement) => boolean) =>
  isInside(isComponentRoot(componentMap));

export const isInsideUserComponentRoot = (
  componentMap: Component.ComponentMap
): ((root: Expression) => (element: ExpressionElement) => boolean) =>
  isInside((element: ExpressionElement) =>
    isUserComponentRoot(componentMap, element)
  );

export const isInsideUserComponentRootOrLocalesWrapper = (
  componentMap: Component.ComponentMap
): ((root: Expression) => (element: ExpressionElement) => boolean) =>
  isInside(
    (element: ExpressionElement) =>
      isUserComponentRoot(componentMap, element) || isLocalesWrapper(element)
  );

export const isInsideAnyDynamicList: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isDynamicList);

export const isInsideSlot: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isSlot);

export const isInsidePagination: (
  root: Expression
) => (element: ExpressionElement) => boolean = isInside(isPagination);
